import DateFnsUtils from "@date-io/date-fns";
import Box from "@material-ui/core/Box";
import CssBaseline from "@material-ui/core/CssBaseline";
import { ThemeProvider } from "@material-ui/core/styles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import Head from "next/head";
import { Router } from "next/router";
import React, { useEffect, useRef, useState } from "react";
import { ReactQueryConfigProvider } from "react-query";
import { ReactQueryDevtools } from "react-query-devtools";
import analytics from "../analytics";
import AppTheme from "../AppTheme";
import { useAppContext } from "../context/AppContext";
import { useUserContext } from "../context/UserContext";
import { useOurRouter } from "../hooks/useOurRouter";
import { QueryState } from "../types/query";
import { getLocalStorage, setLocalStorage } from "../utils/local-storage";
import Loading from "./Loading";

const NODE_ENV = process.env.NODE_ENV;
const REF_PARAM = "utm_term";

export function App({ children }) {
  const router = useOurRouter<{
    op: string; // used by auth
    [REF_PARAM]?: string;
    state?: QueryState | string;
    logout?: string;
    sharedHabit?: string;
  }>();
  const [userState, actions] = useUserContext();

  const { state } = useAppContext();

  const [theme, setTheme] = useState<Theme>(state.theme === "dark" ? AppTheme.Dark : AppTheme.Light);

  useEffect(() => {
    setTheme(state.theme === "dark" ? AppTheme.Dark : AppTheme.Light);
  }, [state.theme]);

  const resizeTimeout = useRef<NodeJS.Timeout | undefined>();

  // Hack to fix vh on mobile browsers
  useEffect(() => {
    if (typeof window !== "undefined") {
      document.documentElement.style.setProperty("--vh", `${window.innerHeight * 0.01}px`);
      document.documentElement.style.setProperty("--vw", `${window.innerWidth * 0.01}px`);

      window.addEventListener("resize", () => {
        if (resizeTimeout.current) {
          clearTimeout(resizeTimeout.current);
          resizeTimeout.current = undefined;
        }

        resizeTimeout.current = setTimeout(() => {
          document.documentElement.style.setProperty("--vh", `${window.innerHeight * 0.01}px`);
          document.documentElement.style.setProperty("--vw", `${window.innerWidth * 0.01}px`);
        }, 25);
      });
    }
  }, []);

  useEffect(() => {
    // stash ref to add to auth state param
    const cookies = document.cookie.split(/;\s*/).reduce((acc: { [key: string]: any }, cookie: string) => {
      const [key, val] = cookie.split("=");
      acc[key] = val;
      return acc;
    }, {});
    const ref = cookies["ref"] || router.query[REF_PARAM];

    if (ref) setLocalStorage("auth.ref", ref);

    const handleHistory = (url: string) => {
      var location = document.createElement("a");
      location.href = url;
      analytics.page({
        url: location.href,
        path: location.pathname,
        search: location.search,
        params: router.query,
      });
    };

    Router.events.on("routeChangeStart", handleHistory);
    return () => {
      Router.events.off("routeChangeStart", handleHistory);
    };
  }, [router.query]);

  useEffect(() => {
    if (userState.user) {
      analytics.identify(userState.user.trackingCode, {
        email: userState.user.email,
        name: userState.user.name,
        referral_code: getLocalStorage("auth.ref"),
      });
    }

    const path = window.location.pathname;

    // redirect to ensure auth cookie is destroyed
    if (router.query.logout) {
      window.location.href = "/login";
      return;
    }

    if (!userState.isAuthenticated && !["loading", "failed", "ok"].includes(userState.status)) {
      (async () => {
        await actions?.load();
      })();
      return;
    }

    // Dont do any redirect till we know what the heck is going on with the user... render will just show loading
    if (["loading", "init"].includes(userState.status)) return;

    // move state to new format for legacy api
    if (typeof router.query.state === "string") {
      router.replace({
        query: {
          ...router.query,
          state: JSON.parse(router.query.state || "{}"),
        },
      });
      return;
    }

    // if we have a redirect in the state, use it
    if (router.query.state && router.query.state.redirect) {
      router.replace(
        {
          pathname: router.query.state.redirect,
          query: {
            ...router.query,
            state: { ...router.query.state, redirect: undefined },
          },
        },
        router.query.state.redirect,
        { shallow: true }
      );
      return;
    }

    // index routing
    if (path === "/") {
      const hasSharedHabit = !!getLocalStorage("habits.sharedPreset");

      router.replace({
        pathname: userState.user?.onboarded
          ? hasSharedHabit
            ? "/habits"
            : "/stats"
          : userState.isAuthenticated
          ? "/onboarding/welcome"
          : "/login",
        query: router.query,
      });
      return;
    }

    // force login when no auth
    if (!userState.isAuthenticated && !["", "/", "/signup", "/login", "/unauthorized"].includes(path)) {
      router.push({
        pathname: "/login",
      });
      return;
    }

    analytics.track("User Authenticated", {
      category: "User",
      nonInteraction: 1,
    });
  }, [actions, userState.isAuthenticated, userState.status, userState.user, router.query]);

  useEffect(() => {
    if (router.query.sharedHabit) {
      try {
        setLocalStorage("habits.sharedPreset", JSON.parse(decodeURIComponent(escape(atob(router.query.sharedHabit)))));
      } catch (e) {
        console.warn("Invalid preset found", router.query.sharedHabit);
      }
    }
  }, [router.query.sharedHabit]);

  return (
    <React.Fragment>
      <Head>
        <title>Reclaim – Defend your life from work</title>
      </Head>
      <ReactQueryConfigProvider
        config={{
          retry: false,
        }}
      >
        <ThemeProvider theme={theme}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <CssBaseline />
            <Box className="app">
              {/* 
               * If any of the following, just display a loading screen.
               - old state format
               - a redirect in the query
               - a route of /
               - still loading the user or trying to
              */}
              {typeof router.query?.state === "string" ||
              router.query.state?.redirect ||
              (typeof window !== "undefined" && window.location.pathname === "/") ? (
                <Loading />
              ) : (
                <>{children}</>
              )}
            </Box>
          </MuiPickersUtilsProvider>
        </ThemeProvider>
      </ReactQueryConfigProvider>

      {typeof window !== "undefined" && NODE_ENV === "development" && <ReactQueryDevtools initialIsOpen={false} />}
    </React.Fragment>
  );
}
